import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MessageSquare, AlertCircle, Trash2, Check, Upload, X } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, query, orderBy, updateDoc, doc, deleteDoc, addDoc } from 'firebase/firestore';

export default function AdminReviews() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newReview, setNewReview] = useState({
    user_name: '',
    title: '',
    comment: '',
    rating: 5,
    project_location: '',
    area: '',
    project_style: '',
    is_approved: true
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  // 리뷰 데이터 가져오기
  const fetchReviews = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch all reviews and filter client-side to avoid index requirement
      const reviewsQuery = query(collection(db, 'reviews'), orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(reviewsQuery);
      const allReviewsData = [];
      querySnapshot.forEach((doc) => {
        allReviewsData.push({ id: doc.id, ...doc.data() });
      });

      // Apply filter client-side
      let filteredReviews = allReviewsData;
      if (filter === 'verified') {
        filteredReviews = allReviewsData.filter(review => review.is_approved === true);
      } else if (filter === 'unverified') {
        filteredReviews = allReviewsData.filter(review => review.is_approved === false);
      }

      setReviews(filteredReviews);
    } catch (err) {
      console.warn('Reviews fetch failed:', err);
      setReviews([]);
      setError('리뷰를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // 리뷰 상태 업데이트
  const updateReview = async (id, approved) => {
    try {
      setIsUpdating(true);
      const reviewDoc = doc(db, 'reviews', id);
      await updateDoc(reviewDoc, { is_approved: approved });

      // 성공 시 목록 새로고침
      fetchReviews();
    } catch (error) {
      console.error('Review update failed:', error);
      alert('리뷰 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  // 리뷰 삭제
  const deleteReview = async (id) => {
    try {
      if (window.confirm('정말로 이 리뷰를 삭제하시겠습니까?')) {
        setIsDeleting(true);
        const reviewDoc = doc(db, 'reviews', id);
        await deleteDoc(reviewDoc);

        // 성공 시 목록 새로고침
        fetchReviews();
      }
    } catch (error) {
      console.error('Review delete failed:', error);
      alert('리뷰 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  // 리뷰 추가 모달 열기
  const handleAddReview = () => {
    setIsAddModalOpen(true);
  };

  // 리뷰 추가 모달 닫기
  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setNewReview({
      user_name: '',
      title: '',
      comment: '',
      rating: 5,
      project_location: '',
      area: '',
      project_style: '',
      is_approved: true
    });
    setSelectedImages([]);
    setImagePreviews([]);
  };

  // 이미지 파일 선택 처리 (여러 파일 지원)
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    handleImages(files);
  };

  // 드래그 앤 드롭 이벤트 처리
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleImages(files);
  };

  // 드래그 오버 이벤트 처리
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // 이미지 처리 함수
  const handleImages = (files) => {
    const validFiles = [];
    const previews = [];
    
    files.forEach((file) => {
      // 파일 크기 체크 (5MB 제한)
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name}: 이미지 파일 크기는 5MB 이하여야 합니다.`);
        return;
      }

      // 파일 타입 체크
      if (!file.type.startsWith('image/')) {
        alert(`${file.name}: 이미지 파일만 업로드 가능합니다.`);
        return;
      }

      validFiles.push(file);
      
      // 미리보기 생성
      const reader = new FileReader();
      reader.onload = (e) => {
        previews.push({ file, preview: e.target?.result });
        if (previews.length === validFiles.length) {
          // 모든 미리보기가 로드되면 상태 업데이트
          const sortedPreviews = validFiles.map(file => 
            previews.find(p => p.file === file)
          ).filter(Boolean);
          
          setSelectedImages(prev => [...prev, ...validFiles]);
          setImagePreviews(prev => [...prev, ...sortedPreviews.map(p => p.preview)]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // 이미지를 base64로 변환
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // 이미지 제거
  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // 리뷰 추가 제출
  const handleAddReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsAdding(true);
      
      // Validate required fields
      if (!newReview.user_name || !newReview.title || !newReview.comment) {
        alert('필수 항목(*)을 입력해주세요.');
        setIsAdding(false);
        return;
      }
      
      // Handle multiple image uploads
      const imageUrls = [];
      if (selectedImages.length > 0) {
        // Process images sequentially to avoid memory issues
        for (const image of selectedImages) {
          const imageUrl = await convertImageToBase64(image);
          imageUrls.push(imageUrl);
        }
      }
      
      // Add created_at timestamp
      const reviewData = {
        ...newReview,
        image_urls: imageUrls, // 여러 이미지를 배열로 저장
        created_at: new Date().toISOString()
      };
      
      // Add to Firestore
      await addDoc(collection(db, 'reviews'), reviewData);
      
      // Refresh reviews list
      fetchReviews();
      
      // Close modal and reset form
      closeAddModal();
      
      alert('리뷰가 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('Review add failed:', error);
      alert('리뷰 추가 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsAdding(false);
    }
  };

  const averageRating = reviews.length > 0 ?
    reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length : 0;

  const unapprovedReviews = reviews.filter((r) => r.is_approved === false);
  const approvedReviews = reviews.filter((r) => r.is_approved === true);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-red-600">데이터를 불러오는 중 오류가 발생했습니다.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">리뷰 관리</h1>
          <p className="text-sm text-gray-600 mt-1">고객 리뷰를 관리하고 인증합니다</p>
        </div>
        <button
          onClick={handleAddReview}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          리뷰 추가
        </button>
      </div>

      {/* 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">전체 리뷰</p>
              <p className="text-2xl font-bold text-gray-900">{reviews.length}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">평균 평점</p>
              <p className="text-2xl font-bold text-gray-900">{averageRating.toFixed(1)}</p>
            </div>
            <Star className="w-8 h-8 text-yellow-500 fill-current" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">승인 대기</p>
              <p className="text-2xl font-bold text-gray-900">{unapprovedReviews.length}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            전체 ({reviews.length})
          </button>
          <button
            onClick={() => setFilter('verified')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'verified' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            승인됨 ({approvedReviews.length})
          </button>
          <button
            onClick={() => setFilter('unverified')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'unverified' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            승인 대기 ({unapprovedReviews.length})
          </button>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">등록된 리뷰가 없습니다</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'
                              }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {review.rating}점
                      </span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${review.is_approved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {review.is_approved ? '승인됨' : '승인 대기'}
                    </div>
                  </div>

                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    {review.title}
                  </h3>

                  <div className="flex items-center text-sm text-gray-600 mb-3 space-x-4">
                    <span className="font-medium">{review.user_name}</span>
                    {review.project_location && (
                      <>
                        <span>•</span>
                        <span>{review.project_location}</span>
                      </>
                    )}
                    {review.area && (
                      <>
                        <span>•</span>
                        <span>{review.area}평</span>
                      </>
                    )}
                    {review.project_style && (
                      <>
                        <span>•</span>
                        <span>{review.project_style}</span>
                      </>
                    )}
                  </div>

                  <p className="text-gray-700 text-sm mb-3 leading-relaxed">
                    {review.comment}
                  </p>
                  {review.image_urls && review.image_urls.length > 0 && (
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      {review.image_urls.map((imageUrl, index) => (
                        <img
                          key={index}
                          src={imageUrl}
                          alt={`Review ${index + 1}`}
                          className="w-full h-auto rounded-md border border-gray-300 max-h-40 object-contain"
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex items-center text-xs text-gray-500">
                    <span>📅 {new Date(review.created_at).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                  </div>
                </div>

                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={() => updateReview(review.id, !review.is_approved)}
                    disabled={isUpdating}
                    className={`px-3 py-1 rounded text-xs transition-colors disabled:opacity-50 ${review.is_approved
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                  >
                    <Check className="w-3 h-3 inline mr-1" />
                    {isUpdating ? '처리 중...' : (review.is_approved ? '승인 취소' : '승인')}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('정말로 이 리뷰를 삭제하시겠습니까?')) {
                        deleteReview(review.id);
                      }
                    }}
                    disabled={isDeleting}
                    className="px-3 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" />
                    {isDeleting ? '삭제 중...' : '삭제'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 리뷰 추가 모달 */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">리뷰 추가</h3>
                <button
                  onClick={closeAddModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleAddReviewSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">작성자명 *</label>
                    <input
                      type="text"
                      value={newReview.user_name}
                      onChange={(e) => setNewReview({...newReview, user_name: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                    <input
                      type="text"
                      value={newReview.title}
                      onChange={(e) => setNewReview({...newReview, title: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">리뷰 내용 *</label>
                    <textarea
                      value={newReview.comment}
                      onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                      rows={4}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">평점</label>
                    <select
                      value={newReview.rating}
                      onChange={(e) => setNewReview({...newReview, rating: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <option key={star} value={star}>{star}점</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">프로젝트 위치</label>
                    <input
                      type="text"
                      value={newReview.project_location}
                      onChange={(e) => setNewReview({...newReview, project_location: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="서울특별시 강남구"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">면적</label>
                    <input
                      type="text"
                      value={newReview.area}
                      onChange={(e) => setNewReview({...newReview, area: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="30평"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">프로젝트 스타일</label>
                    <input
                      type="text"
                      value={newReview.project_style}
                      onChange={(e) => setNewReview({...newReview, project_style: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="모던 스타일"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">승인 상태</label>
                    <select
                      value={newReview.is_approved}
                      onChange={(e) => setNewReview({...newReview, is_approved: e.target.value === 'true'})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={true}>승인됨</option>
                      <option value={false}>승인 대기</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">리뷰 이미지</label>
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={() => document.getElementById('image-upload')?.click()}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 mb-1">이미지를 드래그하거나 클릭하여 업로드</p>
                      <p className="text-xs text-gray-500">최대 5MB 이미지 파일 (JPG, PNG, GIF) - 여러 파일 선택 가능</p>
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        multiple
                      />
                    </div>
                    
                    {/* 이미지 미리보기 */}
                    {imagePreviews.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-md border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(index);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                    disabled={isAdding}
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={isAdding}
                  >
                    {isAdding ? '추가 중...' : '리뷰 추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
